import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { openBlobInNewTab } from "../utils/openBlob";
import { isSalesProductAccount } from "../utils/salesAccounts";
import AccountPicker from "../components/AccountPicker";
import { salesAccountGroups } from "../utils/accountGroups";

function fmt(n) { return (n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; }

// Pipeline à 2 niveaux (natif -> IA) — l'ancien palier Tesseract
// intermédiaire (2) a été retiré, jamais fonctionnel en production.
const OCR_LEVEL_LABEL = { 1: 'PDF natif', 3: 'IA' };

const KIND_LABEL = { facture: "Facture", acompte: "Facture d'acompte", avoir: "Avoir" };
const parseJson = (v, d) => { try { return v ? JSON.parse(v) : d; } catch { return d; } };

// Facture à plusieurs taux de TVA : TVA de chaque taux, telle qu'elle figure
// sur la facture (jamais un taux moyen)
// Répartition du HT entre les comptes de produit du plan de la société (70, et 751, 752, 758 : comptes de vente par ligne)
const eur2 = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
function AccountSplitEditor({ inv, accounts, onSaved, onError }) {
  const init = (inv.account_split && inv.account_split.length ? inv.account_split : [{ account: "", ht: "" }]).map(x => ({ account: String(x.account || ""), ht: x.ht === "" ? "" : String(x.ht) }));
  const [rows, setRows] = useState(init);
  const [busy, setBusy] = useState(false);
  const total = Math.round(rows.reduce((s, r) => s + (parseFloat(String(r.ht).replace(",", ".")) || 0), 0) * 100) / 100;
  const expected = Number(inv.expected_split_ht || 0);
  const gap = Math.round((expected - total) * 100) / 100;
  const save = async () => {
    setBusy(true);
    try { await api.salesOcrInvoices.update(inv.id, { account_split: rows.filter(r => r.account || r.ht).map(r => ({ account: r.account, ht: r.ht })) }); onSaved(); }
    catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 8, padding: "8px 10px", background: "#f8fafc", borderRadius: 8, fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Comptes de vente {inv.account_split_source === "journal" ? "(repris du journal des ventes Dolibarr)" : inv.account_split_source === "user" ? "(répartition saisie)" : ""} — HT à répartir : {eur2(expected)}</div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}>
          {/* Recherche par numéro ou intitulé, groupes Ventes (70) / Autres produits (75) (A1b-2) */}
          <AccountPicker value={r.account} width={110} ariaLabel="Compte de produit" placeholder="compte de produit" {...salesAccountGroups}
            choices={accounts.map(a => ({ ...a, kind: "general" }))} onCommit={v => setRows(rs => rs.map((x, j) => (j === i ? { ...x, account: v } : x)))} />
          {r.account && !accounts.some(a => a.account_code === r.account) && <span style={{ fontSize: 11, color: "#b45309" }}>absent du plan</span>}
          <input className="input" type="number" step="0.01" style={{ width: 110, fontSize: 12, padding: "2px 6px" }} value={r.ht} placeholder="HT" onChange={e => setRows(rs => rs.map((x, j) => (j === i ? { ...x, ht: e.target.value } : x)))} />
          <button className="btn" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => setRows(rs => (rs.length > 1 ? rs.filter((_, j) => j !== i) : [{ account: "", ht: "" }]))}>✕</button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
        <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => setRows(rs => [...rs, { account: "", ht: gap > 0 ? String(gap) : "" }])}>+ Ligne</button>
        <span style={{ color: Math.abs(gap) <= 0.02 ? "#16a34a" : "#b45309" }}>Total {eur2(total)}{Math.abs(gap) > 0.02 ? ` — reste à répartir ${eur2(gap)}` : " ✓"}</span>
        <button className="btn primary" style={{ fontSize: 11, padding: "3px 10px" }} disabled={busy} onClick={save}>Enregistrer la répartition</button>
      </div>
    </div>
  );
}

function VatBreakdownEditor({ inv, onSaved, onError }) {
  const [vals, setVals] = useState({ "20": "", "10": "", "5.5": "", "2.1": "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const lines = Object.entries(vals).filter(([, v]) => v !== "" && !isNaN(parseFloat(String(v).replace(",", ".")))).map(([rate, vat]) => ({ rate, vat: String(vat).replace(",", ".") }));
      await api.salesOcrInvoices.update(inv.id, { vat_breakdown: lines });
      onSaved && onSaved();
    } catch (e) { onError && onError(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 6, fontSize: 12, padding: "6px 8px", background: "#fff7ed", borderRadius: 6 }}>
      Plusieurs taux de TVA : indiquez la TVA de chaque taux (total {Number(inv.amount_tva || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €) —{" "}
      {Object.keys(vals).map(rate => (
        <span key={rate} style={{ marginRight: 8 }}>
          {rate.replace(".", ",")} %{" "}
          <input className="input" type="number" step="0.01" value={vals[rate]} onChange={e => setVals(v => ({ ...v, [rate]: e.target.value }))}
            style={{ display: "inline-block", width: 80, fontSize: 12, padding: "2px 6px" }} />
        </span>
      ))}
      <button className="btn" style={{ fontSize: 11, padding: "2px 8px" }} disabled={busy} onClick={save}>{busy ? "…" : "Enregistrer"}</button>
    </div>
  );
}

export default function SalesInvoiceOcrTab({ companyId, onImport, onOpenDeposits }) {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState("");
  const [editId, setEditId]       = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [actionId, setActionId]   = useState(null);
  const [vatWarning, setVatWarning] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [batchNature, setBatchNature] = useState("");
  const [plan70, setPlan70] = useState([]);
  const [journalResult, setJournalResult] = useState(null);
  const [journalBusy, setJournalBusy] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    api.salesOcrInvoices.list(companyId, { status: 'staging', limit: 500 })
      .then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);
  // Comptes 70 du plan de la société (liste de la répartition par compte)
  useEffect(() => { if (companyId) api.chartAccounts.list(companyId).then(l => setPlan70((l || []).filter(a => isSalesProductAccount(a.account_code)))).catch(() => setPlan70([])); }, [companyId]);
  const planMode = items.some(i => i.account_split_required);
  const importJournal = async (file) => {
    if (!file) return;
    setJournalBusy(true); setError(""); setJournalResult(null);
    try {
      const b64 = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(",")[1] || ""); r.onerror = reject; r.readAsDataURL(file); });
      setJournalResult(await api.salesOcrInvoices.importJournal(companyId, { filename: file.name, content_base64: b64 }));
      load();
    } catch (e) { setError(e.message); }
    finally { setJournalBusy(false); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !companyId) return;
    setUploading(true); setError("");
    try {
      const r = await api.salesOcrInvoices.upload(companyId, file);
      const dupRefs = (r.results || []).filter(x => x.status === "duplicate").map(x => x.invoice_ref).filter(Boolean);
      const errRefs = (r.results || []).filter(x => x.status === "error");
      if (r.warning?.message) {
        setError(r.warning.message);
      } else if (r.duplicates > 0 || r.errors > 0) {
        let msg = `${r.imported}/${r.total} facture(s) importée(s).`;
        if (dupRefs.length > 0) msg += ` Déjà importée(s) : ${dupRefs.join(", ")}.`;
        if (errRefs.length > 0) msg += ` ${errRefs.length} erreur(s).`;
        setError(msg);
      } else if (r.imported > 0) {
        setError('');
      }
      load();
    } catch (err) {
      setError(err?.message || "Erreur lors de l'import.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const startEdit = (inv) => {
    setEditId(inv.id);
    setEditForm({
      client_name: inv.client_name || "", invoice_ref: inv.invoice_ref || "",
      invoice_date: inv.invoice_date || "", amount_ht: inv.amount_ht ?? "",
      amount_tva: inv.amount_tva ?? "", amount_ttc: inv.amount_ttc ?? "",
    });
  };

  const saveEdit = async (id) => {
    try {
      await api.salesOcrInvoices.update(id, editForm);
      setEditId(null); load();
    } catch (err) { setError(err?.message || "Erreur."); }
  };

  const handleValidate = async (inv) => {
    setActionId(inv.id); setError("");
    try {
      const r = await api.salesOcrInvoices.validate(inv.id, {});
      if (r?.warning) setVatWarning(r.warning);
      load();
    } catch (err) {
      setError(err?.message || "Erreur lors de la validation.");
    } finally { setActionId(null); }
  };

  // Validation par lot : seules les factures sans anomalie (le serveur re-contrôle)
  const NATURE_TODO = "nature biens/services à choisir";
  const eligible = items.filter(i => Array.isArray(i.anomalies) && (i.anomalies.length === 0 || (batchNature && i.anomalies.every(a => a === NATURE_TODO))));
  const withoutNature = items.filter(i => Array.isArray(i.anomalies) && i.anomalies.includes(NATURE_TODO)).length;
  const handleValidateBatch = async () => {
    if (!window.confirm(`Valider et comptabiliser ${eligible.length} facture(s) sans anomalie${batchNature ? ` (nature « ${batchNature === "biens" ? "Ventes de biens" : "Prestations de services"} » appliquée à celles qui n'en ont pas)` : ""} ?`)) return;
    setBatchBusy(true); setError(""); setBatchResult(null);
    try {
      const r = await api.salesOcrInvoices.validateBatch(companyId, eligible.map(i => i.id), batchNature || undefined);
      setBatchResult(r);
      if (r?.warning) setVatWarning(r.warning);
      load();
    } catch (err) { setError(err?.message || "Erreur lors de la validation par lot."); }
    finally { setBatchBusy(false); }
  };

  const handleReject = async (inv) => {
    setActionId(inv.id);
    try {
      await api.salesOcrInvoices.reject(inv.id, 'Rejeté manuellement');
      load();
    } catch (err) { setError(err?.message || "Erreur."); }
    finally { setActionId(null); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Factures à valider</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Import OCR ou commande expédiée → vérification → comptabilisation. Rien n'est comptabilisé avant validation manuelle.</div>
        </div>
        {onImport ? (
          <div style={{ display: "flex", gap: 8 }}>
            {planMode && (
              <label className="btn" style={{ cursor: journalBusy ? "wait" : "pointer" }} title="Journal des ventes exporté de Dolibarr : il pré-remplit le compte 70 de chaque facture">
                {journalBusy ? "Lecture du journal…" : "📒 Journal des ventes Dolibarr"}
                <input type="file" accept=".csv,.txt,.xlsx" style={{ display: "none" }} disabled={journalBusy} onChange={e => { importJournal(e.target.files[0]); e.target.value = ""; }} />
              </label>
            )}
            <button className="btn primary" onClick={onImport}>📥 Importer des factures de vente</button>
          </div>
        ) : (
          <label className="btn primary" style={{ cursor: uploading ? "wait" : "pointer" }}>
            {uploading ? "Analyse en cours…" : "+ Importer une facture PDF"}
            <input type="file" accept="application/pdf" onChange={handleFileSelect} disabled={uploading} style={{ display: "none" }} />
          </label>
        )}
      </div>

      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {journalResult && (
        <div style={{ fontSize: 12.5, padding: "8px 12px", background: "#f0fdf9", borderRadius: 8, marginBottom: 12 }}>
          Journal des ventes : comptes repris pour {journalResult.applied} facture(s){journalResult.kept_user ? `, ${journalResult.kept_user} répartition(s) saisie(s) conservée(s)` : ""}{journalResult.deposit_invoices ? `, ${journalResult.deposit_invoices} facture(s) d'acompte (419, sans compte 70)` : ""}.
          {journalResult.not_in_journal.length > 0 && <> Absentes du journal (à répartir à la main) : {journalResult.not_in_journal.join(", ")}.</>}
          {journalResult.accounts_not_in_plan.length > 0 && <span style={{ color: "#b45309" }}> Comptes absents du plan : {journalResult.accounts_not_in_plan.join(", ")}.</span>}
          {journalResult.mismatches.length > 0 && <span style={{ color: "#b45309" }}> Écart de HT : {journalResult.mismatches.map(m => m.invoice_ref).join(", ")}.</span>}
          {journalResult.vat_breakdown_filled > 0 && <> TVA par taux reprise pour {journalResult.vat_breakdown_filled} facture(s).</>}
          {journalResult.vat_breakdown_mismatch?.length > 0 && <span style={{ color: "#b45309" }}> TVA du journal différente de la facture (détail à saisir) : {journalResult.vat_breakdown_mismatch.join(", ")}.</span>}
          {journalResult.vat_accounts_unknown?.length > 0 && <span style={{ color: "#b45309" }}> Comptes de TVA sans taux réglé : {journalResult.vat_accounts_unknown.join(", ")}.</span>}
        </div>
      )}
      {vatWarning && (
        <div role="alert" style={{ background: "#f59e0b22", color: "#b45309", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", gap: 8, justifyContent: "space-between" }}>
          <span>⚠️ {vatWarning}</span>
          <button type="button" onClick={() => setVatWarning("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }} aria-label="Fermer">✕</button>
        </div>
      )}

      {items.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12, padding: "10px 14px", background: "#f0fdf9", borderRadius: 10, fontSize: 13 }}>
          <span><strong>{eligible.length}</strong> facture(s) sans anomalie sur {items.length}{items.length - eligible.length > 0 ? ` — ${items.length - eligible.length} à vérifier une par une (motif indiqué sur chaque facture)` : ""}.</span>
          {withoutNature > 0 && (
            <label style={{ fontSize: 12 }}>
              Nature des {withoutNature} facture(s) sans nature :{" "}
              <select className="input" style={{ display: "inline-block", width: "auto", fontSize: 12, padding: "2px 6px" }} value={batchNature} onChange={e => setBatchNature(e.target.value)}>
                <option value="">— à choisir une par une —</option>
                <option value="services">Prestations de services</option>
                <option value="biens">Ventes de biens</option>
              </select>
            </label>
          )}
          <button className="btn primary" disabled={!eligible.length || batchBusy} onClick={handleValidateBatch}>
            {batchBusy ? "Validation en cours…" : `✓ Valider les ${eligible.length} factures sans anomalie`}
          </button>
        </div>
      )}
      {batchResult && (
        <div style={{ fontSize: 12.5, padding: "8px 12px", background: "#f9fafb", borderRadius: 8, marginBottom: 12 }}>
          {batchResult.validated.length} facture(s) validée(s) et comptabilisée(s){batchResult.skipped.length ? `, ${batchResult.skipped.length} écartée(s) (anomalie)` : ""}{batchResult.errors.length ? `, ${batchResult.errors.length} en erreur : ${batchResult.errors.map(e => `${e.invoice_ref || "?"} (${e.error})`).join(" ; ")}` : ""}.
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>Chargement…</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#6b7280", background: "#f9fafb", borderRadius: 10 }}>
          Aucune facture en attente. Importez un PDF, ou attendez qu'une commande passe à "Expédiée" dans Stocks &amp; Inventaire.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map(inv => {
            const editing = editId === inv.id;
            const busy = actionId === inv.id;
            const fromOrder = inv.source === 'stock_order';
            // Une facture générée depuis une commande expédiée reçoit son
            // numéro à la validation (séquence atomique, cf. server/routes/
            // salesInvoicesStaging.js) — contrairement à l'OCR, l'absence de
            // invoice_ref n'y bloque donc jamais la validation.
            const missingRef = !inv.invoice_ref && !fromOrder;
            return (
              <div key={inv.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 18px" }}>
                {inv.anomalies?.length > 0 && (
                  <div style={{ fontSize: 11.5, color: "#b45309", marginBottom: 6 }}>⚠ À vérifier : {inv.anomalies.join(" · ")}
                    {/* B7 : acompte antérieur à l'exercice → joindre son PDF (aperçu dans « Acomptes d'ouverture ») */}
                    {onOpenDeposits && inv.anomalies.some(a => /antérieur au début d.exercice/.test(a)) && (
                      <button className="btn" style={{ fontSize: 11, padding: "1px 8px", marginLeft: 8 }} onClick={onOpenDeposits}>Joindre le PDF de l'acompte</button>)}</div>
                )}
                {editing ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600 }}>Client</label>
                      <input className="input" value={editForm.client_name} onChange={e => setEditForm(f => ({ ...f, client_name: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600 }}>N° facture *</label>
                      <input className="input" value={editForm.invoice_ref} onChange={e => setEditForm(f => ({ ...f, invoice_ref: e.target.value }))} placeholder="Numéro légal, obligatoire" />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600 }}>Date</label>
                      <input className="input" type="date" value={editForm.invoice_date} onChange={e => setEditForm(f => ({ ...f, invoice_date: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600 }}>HT</label>
                      <input className="input" type="number" step="0.01" value={editForm.amount_ht} onChange={e => setEditForm(f => ({ ...f, amount_ht: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600 }}>TVA</label>
                      <input className="input" type="number" step="0.01" value={editForm.amount_tva} onChange={e => setEditForm(f => ({ ...f, amount_tva: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600 }}>TTC</label>
                      <input className="input" type="number" step="0.01" value={editForm.amount_ttc} onChange={e => setEditForm(f => ({ ...f, amount_ttc: e.target.value }))} />
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {inv.client_name || "Client non identifié"}
                        {fromOrder
                          ? <span style={{ color: "#6b7280", fontWeight: 400 }}> · N° généré à la validation</span>
                          : inv.invoice_ref
                            ? <span style={{ color: "#6b7280", fontWeight: 400 }}> · Réf. {inv.invoice_ref}</span>
                            : <span style={{ color: "#e24b4a", fontWeight: 600 }}> · N° facture manquant</span>}
                        {/* 📎 PDF d'origine (A1b) ; sans PDF : « PDF absent », les montants sont affichés dessous */}
                        {inv.file_url || inv.file_name
                          ? <button className="btn" style={{ fontSize: 11, padding: "1px 6px", marginLeft: 8 }} onClick={() => openBlobInNewTab(() => api.salesOcrInvoices.file(inv.id))} title="Ouvrir le PDF de la facture">📎</button>
                          : <span style={{ fontSize: 11, fontWeight: 600, color: "#b45309", marginLeft: 8 }}>PDF absent</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        {fmtDate(inv.invoice_date)} · HT {fmt(inv.amount_ht)} € · TVA {fmt(inv.amount_tva)} € · TTC {fmt(inv.amount_ttc)} €
                        {inv.is_paid ? " · déjà réglée" : ""}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                        {fromOrder ? (
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#e0e7ff", color: "#3730a3", fontWeight: 700 }}>
                            📦 Commande {inv.source_order_number || `#${inv.source_order_id}`}
                          </span>
                        ) : (
                          <>
                            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#e0e7ff", color: "#3730a3", fontWeight: 700 }}>
                              {OCR_LEVEL_LABEL[inv.ocr_level] || "Niveau inconnu"}
                            </span>
                            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: inv.confidence_score >= 0.8 ? "#dcfce7" : "#fef3c7", color: inv.confidence_score >= 0.8 ? "#166534" : "#92400e", fontWeight: 700 }}>
                              Confiance {Math.round((inv.confidence_score || 0) * 100)}%
                            </span>
                            {inv.suggested_customer_id && (
                              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#dcfce7", color: "#166534", fontWeight: 700 }}>Client reconnu</span>
                            )}
                          </>
                        )}
                        {inv.doc_kind && KIND_LABEL[inv.doc_kind] && (
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: inv.doc_kind === "avoir" ? "#fee2e2" : inv.doc_kind === "acompte" ? "#fef3c7" : "#dbeafe", color: "#1f2937", fontWeight: 700 }}>{KIND_LABEL[inv.doc_kind]}</span>
                        )}
                        {(inv.source === "dolibarr" || inv.source === "dolibarr_export") && (
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#ede9fe", color: "#5b21b6", fontWeight: 700 }}>Dolibarr{inv.source === "dolibarr_export" ? " (export)" : ""}</span>
                        )}
                        {inv.order_ref && (
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#f3f4f6", color: "#374151", fontWeight: 700 }}>Commande {inv.order_ref}</span>
                        )}
                        {inv.client_account_code && (
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#f3f4f6", color: "#374151", fontFamily: "monospace", fontWeight: 700 }}>{inv.client_account_code}</span>
                        )}
                        {inv.client_type && (
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#f3f4f6", color: "#374151", fontWeight: 700 }}>{inv.client_type === "particulier" ? "Particulier" : "Entreprise"}</span>
                        )}
                        {inv.proposed_account_code && (
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#f3f4f6", color: "#374151", fontWeight: 700 }} title="Classe comptable PCG proposée à la validation">
                            📊 {inv.proposed_account_code} — {inv.proposed_account_label}
                          </span>
                        )}
                      </div>
                      {inv.match_status === "to_confirm" && !inv.customer_id && (
                        <div style={{ marginTop: 8, padding: "6px 10px", background: "#fff7ed", borderRadius: 8, fontSize: 12 }}>
                          <strong>Client à confirmer</strong> (aucune fusion automatique) :{" "}
                          <select className="input" style={{ display: "inline-block", width: "auto", fontSize: 12, padding: "2px 6px" }} defaultValue=""
                            onChange={async e => { if (!e.target.value) return; try { await api.salesOcrInvoices.update(inv.id, { client_choice: e.target.value }); load(); } catch (err) { setError(err.message); } }}>
                            <option value="">— choisir —</option>
                            {parseJson(inv.match_candidates, []).map(c => <option key={c.id} value={c.id}>{c.name}{c.postal_code ? ` (${c.postal_code})` : ""} — {c.reason}</option>)}
                            <option value="new">Nouveau client « {inv.client_name} »</option>
                          </select>
                        </div>
                      )}
                      {["new", "new_chosen"].includes(inv.match_status) && !inv.customer_id && (inv.source === "dolibarr" || inv.source === "dolibarr_export") && (
                        <div style={{ marginTop: 6, fontSize: 11.5, color: "#6b7280" }}>Nouveau client : sa fiche sera créée à la validation avec les données de la facture.</div>
                      )}
                      {/* Mode reprise d'activité : code client de l'expert-comptable, facultatif (28/10/2026) */}
                      {inv.client_code_optional && (
                        <div style={{ marginTop: 6, padding: "6px 10px", background: "#fff7ed", borderRadius: 8, fontSize: 12 }}>
                          <strong>Code de l'expert-comptable</strong> (facultatif) :{" "}
                          <input className="input" style={{ display: "inline-block", width: 140, fontSize: 12, padding: "2px 6px" }} placeholder="ex. 411CARBL" defaultValue={inv.client_account_code || ""}
                            onBlur={async e => { const v = e.target.value.trim().toUpperCase(); if (v === (inv.client_account_code || "")) return; try { await api.salesOcrInvoices.update(inv.id, { client_account_code: v }); load(); } catch (err) { setError(err.message); } }} />
                          <span style={{ color: "#6b7280" }}> — {inv.client_account_code ? `${inv.pdf_code?.status === "proposé" && inv.pdf_code.code === inv.client_account_code ? "lu sur le PDF, " : ""}repris sur la fiche du client à la validation.` : "vide : compte automatique, mention « code expert à attribuer »."}</span>
                        </div>
                      )}
                      {/* Code lu sur le PDF Dolibarr (« Code comptable client ») : différent de la fiche ou invalide (28/10/2026) */}
                      {inv.pdf_code?.status === "différent" && (
                        <div style={{ marginTop: 6, padding: "6px 10px", background: "#fef3c7", borderRadius: 8, fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <span>Code du PDF <strong>{inv.pdf_code.code}</strong> différent de la fiche client (<strong>{inv.pdf_code.fiche}</strong>)
                            {inv.pdf_code.choice ? ` — choisi : ${inv.pdf_code.choice === "pdf" ? `code du PDF (appliqué à la validation)` : "code de la fiche"}` : " :"}</span>
                          {["fiche", "pdf"].map(c => (
                            <button key={c} className={`btn${inv.pdf_code.choice === c ? " primary" : ""}`} style={{ fontSize: 11, padding: "2px 8px" }}
                              onClick={async () => { try { await api.salesOcrInvoices.update(inv.id, { pdf_code_choice: c }); load(); } catch (err) { setError(err.message); } }}>
                              {c === "fiche" ? `Garder ${inv.pdf_code.fiche}` : `Prendre ${inv.pdf_code.code}`}
                            </button>
                          ))}
                        </div>
                      )}
                      {inv.pdf_code?.status === "invalide" && (
                        <div style={{ marginTop: 6, fontSize: 11.5, color: "#92400e" }}>⚠ Code comptable lu sur le PDF invalide (« {inv.pdf_code.code || "vide"} ») : ignoré.</div>
                      )}
                      {parseJson(inv.vat_breakdown, []).length > 1 && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#374151" }}>
                          TVA par taux : {parseJson(inv.vat_breakdown, []).filter(x => x.rate).map(x => `${String(x.rate).replace(".", ",")} % : ${Number(x.vat).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`).join(" · ")}
                        </div>
                      )}
                      {inv.account_split_required && <AccountSplitEditor key={`${inv.id}-${JSON.stringify(inv.account_split)}`} inv={inv} accounts={plan70} onSaved={load} onError={setError} />}
                      {inv.anomalies?.some(a => /plusieurs taux/.test(a)) && (
                        <VatBreakdownEditor inv={inv} onSaved={load} onError={setError} />
                      )}
                      {inv.source !== "stock_order" && (
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          Nature :{" "}
                          <select className="input" style={{ display: "inline-block", width: "auto", fontSize: 12, padding: "2px 6px" }}
                            value={inv.operation_nature || ""}
                            onChange={async e => { try { await api.salesOcrInvoices.update(inv.id, { operation_nature: e.target.value }); load(); } catch (err) { setError(err.message); } }}>
                            <option value="" disabled>— À choisir —</option>
                            <option value="services">Prestations de services</option>
                            <option value="biens">Ventes de biens (TVA toujours due à la facture)</option>
                            <option value="mixte">Mixte (prestations et biens sur la même facture)</option>
                          </select>
                          {inv.operation_nature === "mixte" && (
                            <span style={{ marginLeft: 8 }}>
                              Part biens : HT{" "}
                              <input className="input" type="number" step="0.01" defaultValue={inv.ht_biens ?? ""} style={{ display: "inline-block", width: 90, fontSize: 12, padding: "2px 6px" }}
                                onBlur={async e => { try { await api.salesOcrInvoices.update(inv.id, { ht_biens: e.target.value, tva_biens: inv.tva_biens ?? "" }); load(); } catch (err) { setError(err.message); } }} />
                              {" "}TVA{" "}
                              <input className="input" type="number" step="0.01" defaultValue={inv.tva_biens ?? ""} style={{ display: "inline-block", width: 80, fontSize: 12, padding: "2px 6px" }}
                                onBlur={async e => { try { await api.salesOcrInvoices.update(inv.id, { ht_biens: inv.ht_biens ?? "", tva_biens: e.target.value }); load(); } catch (err) { setError(err.message); } }} />
                              <span style={{ color: "#6b7280" }}> (le reste = prestations)</span>
                            </span>
                          )}
                        </div>
                      )}
                      {parseJson(inv.discrepancies, []).length > 0 && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#ba7517" }}>⚠ Écarts avec l'export : {parseJson(inv.discrepancies, []).join(" ; ")}</div>
                      )}
                      {/* État réel des acomptes repris (04/11/2026) */}
                      {inv.deposit_state?.status === "retrouve" && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#0f766e" }}>
                          ✓ Reprend l'acompte {inv.deposit_state.refs.join(", ")} : retrouvé, {Number(inv.deposit_state.total_419).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € déduits à la validation (à valider une par une).
                        </div>
                      )}
                      {inv.deposit_state?.status === "bloque" && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#ba7517" }}>
                          Reprend l'acompte {inv.deposit_state.refs.join(", ")} : validation bloquée tant que l'acompte n'est pas retrouvé (motif en haut de la facture).
                        </div>
                      )}
                      {inv.deposit_state?.status === "avoir" && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#ba7517" }}>
                          Avoir citant l'acompte {inv.deposit_state.refs.join(", ")} : à traiter, pas encore pris en charge (support@execpro360.com).
                        </div>
                      )}
                      {inv.source === "dolibarr_export" && !inv.file_url && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#e24b4a" }}>PDF manquant : importez le PDF de cette facture.</div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {!fromOrder && inv.file_url && (
                        <button className="btn" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => openBlobInNewTab(() => api.salesOcrInvoices.file(inv.id))}>📎 PDF</button>
                      )}
                      <button className="btn" style={{ fontSize: 11 }} onClick={() => startEdit(inv)}>✏️ Corriger</button>
                      <button className="btn" style={{ fontSize: 11, color: "#e24b4a" }} onClick={() => handleReject(inv)} disabled={busy}>✕ Rejeter</button>
                      <button className="btn primary" style={{ fontSize: 11 }} onClick={() => handleValidate(inv)} disabled={busy || missingRef}
                        title={missingRef ? "Numéro de facture manquant — corrigez avant de valider" : ""}>
                        {busy ? "…" : "✓ Valider"}
                      </button>
                    </div>
                  </div>
                )}
                {editing && (
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button className="btn" onClick={() => setEditId(null)}>Annuler</button>
                    <button className="btn primary" onClick={() => saveEdit(inv.id)}>Enregistrer</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
